import { InjectionToken } from '@angular/core';
import { IRynReplayMaskingPolicy } from './ryn-replay-masking-policy';
import { RynReplaySessionEndReason } from './ryn-replay-session-lifecycle.utils';

/**
 * Metadata stamped onto a live replay session when a user is identified.
 */
export interface IRynReplayIdentity {
  /**
   * Optional, and that is #30113. Auth0UserId is a COLUMN on the security user
   * row rather than a token claim, so an Entra-only or newly provisioned user
   * legitimately has no value here while `securityUserId` below is present. It
   * was required, which forced callers to either skip identification entirely or
   * stamp an empty string. An empty string is the worse of the two: it reads as
   * a real value and the RynReplaySession index deliberately filters it out
   * (`Auth0UserId <> N''`), so it would have been a phantom user that no lookup
   * could ever match.
   *
   * At least one of this and `securityUserId` has to be present for an identity
   * to mean anything. That is enforced in RynReplayRecorderService.identify,
   * because a type cannot express "either of these two".
   */
  auth0UserId?: string;
  /**
   * The Software Security user id, a Guid. This is the one that gets stored and
   * indexed, and it is the only identity field the server reads.
   *
   * It is the id worth having because it survives a change of login system.
   * Auth0 tokens carry it on the http://security.universalplant.com/user-id
   * claim and Entra tokens get the same claim injected, so both land on one
   * value, and from that Guid you can reach everything else about the person.
   * `auth0UserId` beside it only means something to Auth0 apps.
   *
   * Comes from `IEmployeeInfo.UserId`, which the Software Security userProfile
   * endpoint fills from the security user row it looked up via that same claim.
   * Optional only because a session exists before anyone has logged in.
   */
  securityUserId?: string;
  name?: string;
  email?: string;
  /** Viewpoint HR reference. Numeric in IEmployeeInfo, string in some DTOs. */
  hrRef?: string | number;
  versionTag?: string;
  buildNumber?: string;
}

export interface IRynReplayStartOptions {
  /** Client-generated session id. Also the last segment of `sessionUrl()`. */
  sessionId: string;
  policy: IRynReplayMaskingPolicy;
  /** From `getAppVersionInfo().versionName` at recording start. */
  appVersion?: string;
  /** From `getAppVersionInfo().buildNumber` at recording start. */
  buildNumber?: string;
  /**
   * Deploy environment label (`dev` / `stg` / `prod`) stamped into the Context
   * event and envelope for session-index filtering (#30211). Resolved from
   * `environment.environment_name` with `production` normalized to `prod`.
   */
  environment?: string;
  /**
   * Called when idle or max-duration rules fire. Must return the new client
   * session id; the backend rotates capture onto it (#31331).
   */
  onSessionRotation?: (
    reason: RynReplaySessionEndReason
  ) => string | Promise<string>;
}

export interface RynReplayEventProperties {
  [key: string]: string | number;
}

/**
 * The recorder boundary. Everything the service knows about actually capturing
 * a session goes through here, which keeps the policy and lifecycle logic
 * testable without a DOM recorder and leaves one place to attach the rrweb
 * implementation.
 *
 * Out of this slice, and all of it attaches behind this interface:
 * - rrweb `record()` wiring inside `start()` / `stop()`, honoring
 *   `policy.maskAllInputs` (rrweb `maskAllInputs`) and `policy.blockClass`
 *   (rrweb `blockClass`).
 * - gzip batching and transport of the emitted event stream to ingest.
 * - IndexedDB offline buffering of batches that fail to send.
 * - console and network devtools capture, with the network side calling
 *   `RynReplayRecorderService.isPayloadRecordingAllowed(url)` before it keeps
 *   any request or response body.
 * - perf-budget measurement around the emit path.
 */
export interface IRynReplayRecorderBackend {
  start(options: IRynReplayStartOptions): void;
  stop(): void;
  pause(): void;
  resume(): void;
  addEvent(name: string, properties?: RynReplayEventProperties): void;
  setIdentity(identity: IRynReplayIdentity): void;
  clearIdentity(): void;
  /**
   * Drain buffered batches to ingest. Pass `unloading: true` only when the
   * document is about to die (logout redirect, pagehide) so transport can use
   * `keepalive` and outlive the page. Everywhere else leave it false — the
   * browser caps keepalive body size and will drop oversized FullSnapshots.
   */
  flush(unloading?: boolean): Promise<void>;
}

/**
 * Default backend. Deliberately does nothing: until the rrweb implementation
 * lands, an app that opts in gets session ids and policy enforcement but no
 * capture, and an app that does not opt in never even reaches this class.
 */
export class NoopRynReplayRecorderBackend implements IRynReplayRecorderBackend {
  start(_options: IRynReplayStartOptions): void {
    void _options;
  }
  stop(): void {
    // no-op
  }
  pause(): void {
    // no-op
  }
  resume(): void {
    // no-op
  }
  addEvent(_name: string, _properties?: RynReplayEventProperties): void {
    void _name;
    void _properties;
  }
  setIdentity(_identity: IRynReplayIdentity): void {
    void _identity;
  }
  clearIdentity(): void {
    // no-op
  }
  flush(_unloading?: boolean): Promise<void> {
    void _unloading;
    return Promise.resolve();
  }
}

export const RYN_REPLAY_RECORDER_BACKEND =
  new InjectionToken<IRynReplayRecorderBackend>('RYN_REPLAY_RECORDER_BACKEND', {
    providedIn: 'root',
    factory: () => new NoopRynReplayRecorderBackend(),
  });
